if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        // Remove all modals that were moved from this quick-add (initial open and any product/variant switches)
        document.querySelectorAll(`[data-related-container-id="${this.id}"]`).forEach((modal) => {
          modal.hasAttribute('open') && modal.hide?.();
          modal.remove();
        });

        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) {
              console.error(
                '[quick-add-modal] Fetched product page has no <product-info>; quick add cannot load.',
              );
              return;
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            this.registerPreprocessCallback();
            this.bindEvents(this.modalContent);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
          });
      }

      preprocessHTML(productElement) {
        if (!productElement) return;
        productElement?.classList?.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      registerPreprocessCallback() {
        const productInfo = this.modalContent.querySelector('product-info');
        if (productInfo?.addPreProcessCallback) {
          productInfo.addPreProcessCallback(this.preprocessHTML.bind(this));
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog:not(.size-guide-modal)');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      getSelectedSizeValue(container) {
        const productInfo = container.querySelector('product-info');
        if (!productInfo || !window.PdpSizeSelection) return null;

        const entry = PdpSizeSelection.getLandedEntry(productInfo);
        const sizeKey = PdpSizeSelection.getSizeOptionKey(entry);
        if (!sizeKey) return null;

        const sizeOption = entry[sizeKey];
        const fieldsets = productInfo.querySelectorAll('variant-selects fieldset');
        const sizeFieldset = fieldsets[sizeOption.position - 1];
        if (!sizeFieldset) return null;

        const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
        return checked?.value ?? null;
      }

      applySizeSelection(container, sizeValue) {
        if (!sizeValue) return;

        const productInfo = container.querySelector('product-info');
        if (!productInfo || !window.PdpSizeSelection) return;

        const entry = PdpSizeSelection.getLandedEntry(productInfo);
        const sizeKey = PdpSizeSelection.getSizeOptionKey(entry);
        if (!sizeKey) return;

        const sizeOption = entry[sizeKey];
        const fieldsets = productInfo.querySelectorAll('variant-selects fieldset');
        const sizeFieldset = fieldsets[sizeOption.position - 1];
        if (!sizeFieldset) return;

        const input = sizeFieldset.querySelector(`input[type="radio"][value="${CSS.escape(sizeValue)}"]`);
        if (!input) return;

        input.checked = true;
        const label = sizeFieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = sizeValue;

        PdpSizeSelection.setPendingSizeSelection(productInfo, sizeValue);
        PdpSizeSelection.syncSubmitButton(productInfo);
      }

      bindEvents(modalContent) {
        // bind color events
        const colors = modalContent.querySelectorAll('.product-form__color-item a');
        if(colors.length > 0) {
          colors.forEach((color) => {
            color.addEventListener('click', (e) => {
              e.preventDefault();

              const previousSize = this.getSelectedSizeValue(this.modalContent);

              fetch(e.target.getAttribute('href'))
              .then((response) => response.text())
              .then((responseText) => {
                const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
                const productElement = responseHTML.querySelector('product-info');
                if (!productElement) {
                  console.error(
                    '[quick-add-modal] Variant switch: no <product-info> in response.',
                  );
                  return;
                }

                this.preprocessHTML(productElement);
                HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

                this.registerPreprocessCallback();
                this.bindEvents(this.modalContent);

                this.applySizeSelection(this.modalContent, previousSize);

                if (window.Shopify && Shopify.PaymentButton) {
                  Shopify.PaymentButton.init();
                }
              });
            })
          })
        }
      }
    }
  );
}

if(!customElements.get('qv-product-media-carousel')) {
  class ProductMediaCarousel extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.options = {}
    }

    initSlider() {
      if (!this.querySelector('[data-slider]')) return;

      this.options = {
        slidesPerView: 'auto',
        spaceBetween: 10,
        navigation: {
          prevEl: this.querySelector('.swiper-button-prev'),
          nextEl: this.querySelector('.swiper-button-next'),
        }
      };

      this.slider = new Swiper(
        this.querySelector('[data-slider]'), // Pass the slider element
        this.options, // Pass the configuration options
      );
    }

    connectedCallback() {
      this.initSlider()
    }
  }
  customElements.define('qv-product-media-carousel', ProductMediaCarousel)
}


const modalCloses = document.querySelectorAll('#modal .modal__close-icon, .drawer-overlay');

modalCloses.forEach((modalClose) => {
  modalClose.addEventListener('click', () => {
    const modalWrapper = document.querySelector('#modal');
    if(modalWrapper.querySelectorAll('.size-guide-tab-container').length > 0) {
      modalWrapper.classList.remove('is-visible');
      modalWrapper.querySelector('.modal__content').innerHTML = '';
      document.querySelector('.drawer-overlay').classList.remove('is-active');
      document.querySelector('.drawer-overlay__inner').classList.remove('is-visible')
    }
  })
});
